<template>
  <div class="order-tracking">
    <div class="order-tracking__heading">
      <TypographyComponent variant="headline-small">Order tracking</TypographyComponent>
      <TypographyComponent variant="body-large">
        Please select your provider below and follow the integration instructions to set up your sales tracking.
      </TypographyComponent>
    </div>
    <div id="order-sales-collection-section" class="order-tracking__providers">
      <div v-if="isGaOrderCollectorEnabled" class="order-tracking__ga">
        <button
          type="button"
          class="order-tracking__tile"
          :class="{ 'order-tracking__tile--active': gaStatus === 1 }"
          @click="authenticateGoogleAnalytics"
        >
          <img v-if="gaLoading" src="../../../assets/loading.svg" alt="loading" class="order-tracking__loading" />
          <img :src="gaLogo" alt="Google Analytics" class="order-tracking__icon-img" />
          <TypographyComponent variant="label-large">Google Analytics</TypographyComponent>
          <span v-if="gaBadge" class="order-tracking__badge" :class="{ 'order-tracking__badge--pending': gaStatus !== 1 }">
            {{ gaBadge }}
          </span>
        </button>
        <TypographyComponent
          link
          variant="label-medium"
          text-decoration="underline"
          text-align="center"
          class="order-tracking__privacy"
          @click="showPrivacyPopup"
        >
          Privacy policy
        </TypographyComponent>
      </div>
      <router-link to="/orders/sales-pixel" class="order-tracking__tile" :class="{ 'order-tracking__tile--active': jpIsActive }">
        <el-icon :size="32"><JavascriptPixelIcon /></el-icon>
        <TypographyComponent variant="label-large">Sales pixel</TypographyComponent>
      </router-link>
      <router-link to="/orders/woo-commerce" class="order-tracking__tile">
        <el-icon :size="32"><WooCommerceIcon /></el-icon>
        <TypographyComponent variant="label-large">WooCommerce</TypographyComponent>
      </router-link>
      <router-link to="/orders/shopify" class="order-tracking__tile">
        <el-icon :size="32"><ShopifyLogo /></el-icon>
        <TypographyComponent variant="label-large">Shopify</TypographyComponent>
      </router-link>
    </div>
    <el-dialog :model-value="collectionDialog !== null" class="add-user-modal" @close="collectionDialog = null">
      <template #header>
        <div class="title">
          <h3 v-if="collectionDialog === 'ga'">Google Analytics Settings</h3>
        </div>
      </template>
      <div class="overflow-hidden">
        <MSelect
          v-if="collectionDialog === 'ga'"
          v-model="gaSelectedPropertyModel"
          label="Select Property:"
          placeholder="Property"
          :col="12"
          :options="
            gaProperties.map((gaProperty) => {
              return { label: gaProperty.name, value: gaProperty.id }
            })
          "
        />
      </div>
      <template #footer>
        <div class="d-flex justify-content-between">
          <el-button round class="white-btn" @click="collectionDialog = null">Cancel</el-button>
          <el-button
            v-if="collectionDialog === 'ga'"
            round
            class="blue-btn"
            @click="
              () => {
                updateSelectedGaProperty()
                collectionDialog = null
              }
            "
          >
            Save
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>
<script lang="ts">
import { useFlag } from '@unleash/proxy-client-vue'
import { ElMessageBox, ElNotification } from 'element-plus'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import JavascriptPixelIcon from '@/assets/icons/JavascriptPixelIcon.svg?component'
import ShopifyIcon from '@/assets/icons/ShopifyIcon.svg?component'
import WooCommerceIcon from '@/assets/icons/WooCommerce.svg?component'
import MSelect from '@/components/MSelect.vue'
import { FLAG_GA_ORDER_COLLECTOR } from '@/lib/constants'
import { config } from '@/lib/helpers'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'
export default {
  name: 'OrderTracking',
  components: {
    MSelect,
    TypographyComponent,
    WooCommerceIcon,
    JavascriptPixelIcon,
    ShopifyLogo: ShopifyIcon,
  },
  setup() {
    return { isGaOrderCollectorEnabled: useFlag(FLAG_GA_ORDER_COLLECTOR) }
  },
  data() {
    return {
      collectionDialog: null,
      googleClient: null,
      gaLogo: new URL('/src/assets/icons/GoogleAnalyticsLogo.svg', import.meta.url).href,
    }
  },
  computed: {
    ...mapGetters('sales', ['gaLoading', 'gaProperties', 'gaSelectedProperty', 'gaIsActive', 'jpIsActive']),
    gaSelectedPropertyModel: {
      get() {
        return this.gaSelectedProperty
      },
      set(value) {
        this.UPDATE_GA_ATTRIBUTE({ type: 'gaSelectedProperty', value })
      },
    },
    gaStatus() {
      const isConnected = this.gaProperties?.length
      if (!isConnected) {
        return 0
      }
      const isSelected = this.gaSelectedProperty || this.gaProperties.find((gaProperty) => gaProperty.selected)
      if (!isSelected) {
        return 3
      }
      if (!this.gaIsActive) {
        return 2
      }
      return 1
    },
    gaBadge() {
      if (this.gaStatus === 1) {
        return 'Active'
      }
      if (this.gaStatus === 2) {
        return 'Pending'
      }
      if (this.gaStatus === 3) {
        return 'Select Property'
      }
      return ''
    },
  },
  created() {
    this.getOrderCollectors()
    this.initGoogleClient()
  },
  methods: {
    ...mapMutations('sales', ['UPDATE_GA_ATTRIBUTE']),
    ...mapActions('sales', ['getOrderCollectors', 'updateSelectedGaProperty', 'connectGoogleAnalytics']),
    authenticateGoogleAnalytics() {
      if (this.gaStatus) {
        this.collectionDialog = 'ga'
        return
      }
      this.googleClient.requestCode()
    },
    async googleCallback(code) {
      const { message, type, properties } = await this.connectGoogleAnalytics(code)
      this.UPDATE_GA_ATTRIBUTE({ type: 'gaProperties', value: properties })
      ElNotification({ title: type === 'success' ? 'Success!' : 'Error', message, type })
      this.collectionDialog = 'ga'
    },
    initGoogleClient() {
      if (config.googleScope) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const context = this
        // @ts-expect-error todo fix me
        const google = window.google
        this.googleClient = google.accounts.oauth2.initCodeClient({
          client_id: config.googleClientId,
          scope: config.googleScope,
          ux_mode: 'popup',
          select_account: true,
          callback: (response) => context.googleCallback(response.code),
        })
      }
    },
    async showPrivacyPopup() {
      await ElMessageBox.alert(
        '<span>The app’s use and transfer to any other app of information received from Google APIs will adhere to </span><a href="https://developers.google.com/terms/api-services-user-data-policy" >Google API Services User Data Policy</a><span>, including the Limited Use requirements.</span>',
        'Privacy policy',
        { dangerouslyUseHTMLString: true },
      )
    },
  },
}
</script>
<style scoped lang="scss">
.order-tracking {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: var(--white);
  border: 1px solid #eee;
  border-radius: 16px;
}
.order-tracking__heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.order-tracking__providers {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
}
.order-tracking__ga {
  flex: 1 0 0;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  .order-tracking__tile {
    flex: 0 0 auto;
    width: 100%;
    min-width: 0;
  }
}
.order-tracking__privacy {
  cursor: pointer;
}
.order-tracking__tile {
  position: relative;
  flex: 1 0 0;
  min-width: 200px;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--white);
  border: 1px solid #eee;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.1s ease-in-out;
  &:hover {
    border-color: #000;
    text-decoration: none;
  }
  &--active {
    border-color: #000;
  }
}
.order-tracking__icon-img {
  width: 32px;
  height: 32px;
}
.order-tracking__loading {
  position: absolute;
  left: 24px;
  width: 30px;
}
.order-tracking__badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 2px 8px;
  background: #0bb07b;
  border-radius: 40px 0 0 40px;
  color: var(--white);
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
  text-transform: uppercase;
  &--pending {
    background: #f5b046;
  }
}
</style>
